<template>
  <BaseShipmentTimelineItem
    :id="`shipment-event-${shipmentEvent.id}`"
    class="shipment-event"
    :class="[
      toCssClassName(shipmentEvent.severity),
      {
        milestone: shipmentEvent.isMilestone,
      },
    ]"
    :event-type="shipmentEvent.eventType"
    :formatted-event-type="titleCase(shipmentEvent.eventType)"
    :subheader="formattedLocation"
    :date-label="
      shipmentEvent.actualDate
        ? t('shipments.dates.actualDateTime')
        : t('events.expectedDateTime')
    "
    :formatted-date="formattedDate"
    :menu-items="menuItems"
    :comment="shipmentEvent.comment"
    :late="shipmentEvent.late"
    :disabled="disabled"
    :hide-menu="hideMenu"
  >
    <template #eventTypeIcon>
      <component
        :is="milestoneIcon"
        v-if="milestoneIcon"
        class="milestone-icon"
      />
    </template>

    <template #eventTypeChip>
      <img
        v-if="shipmentEvent.source === ShipmentEventSource.CONDUIT"
        :src="conduitLogo"
        class="conduit-logo"
      />
      <RygenChip
        v-else
        :label="translateEnum(shipmentEvent.source, 'ShipmentEventSource')"
        :severity="Severity.INFO"
      />
    </template>

    <template v-if="slots.extraRow" #extraRow="slotProps">
      <slot name="extraRow" v-bind="slotProps" />
    </template>
  </BaseShipmentTimelineItem>
</template>

<script lang="ts" setup>
import { computed, PropType, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { renderDateTime, titleCase, translateEnum } from '@rygen/rygen-ui/i18n';
import { RygenMenuItem, Severity } from '@rygen/rygen-ui/model';
import { toCssClassName } from '@rygen/rygen-ui/util';

import conduitLogo from '@/assets/img/conduit_logo.png';
import {
  isPickupOrDropoffType,
  ShipmentEvent,
  ShipmentEventSource,
  ShipmentEventType,
} from '@/model';

import BaseShipmentTimelineItem from '@/components/shipment/BaseShipmentTimelineItem.vue';
import DropoffIcon from '@/components/shippingPoint/DropoffIcon.vue';
import PickupIcon from '@/components/shippingPoint/PickupIcon.vue';
import RygenChip from '@rygen/rygen-ui/components/ui/RygenChip.vue';

const props = defineProps({
  shipmentEvent: {
    type: Object as PropType<ShipmentEvent>,
    required: true,
  },
  removable: {
    type: Boolean,
    default: () => false,
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
  hideMenu: {
    type: Boolean,
    default: () => false,
  },
});
const { shipmentEvent, removable, disabled, hideMenu } = toRefs(props);
const emit = defineEmits<{
  (event: 'delete-shipment-event', shipmentEvent: ShipmentEvent): void;
  (event: 'duplicate-shipment-event', shipmentEvent: ShipmentEvent): void;
  (event: 'edit-shipment-event', shipmentEvent: ShipmentEvent): void;
}>();

const slots = defineSlots<{
  extraRow: (props: { disabled: boolean; late: boolean }) => any;
}>();

const { t } = useI18n();

const isPickupOrDropoffEvent = computed(() =>
  isPickupOrDropoffType(shipmentEvent.value.eventType),
);

const param = t('events.events');
const menuItems = computed((): RygenMenuItem[] => [
  {
    label: t('common.editParam', { param }),
    icon: 'pi pi-fw pi-pencil',
    command: () => emit('edit-shipment-event', shipmentEvent.value),
    requiredAuthorization: ['shipmentEvent', 'update'],
  },
  {
    label: t('common.duplicateParam', { param }),
    icon: 'pi pi-fw pi-copy',
    command: () => emit('duplicate-shipment-event', shipmentEvent.value),
    requiredAuthorization: ['shipmentEvent', 'create'],
  },
  {
    label: t('common.deleteParam', { param }),
    disabled: !removable.value,
    class: 'text-danger',
    icon: 'pi pi-fw pi-trash',
    command: () => emit('delete-shipment-event', shipmentEvent.value),
    requiredAuthorization: ['shipmentEvent', 'delete'],
    visible: !isPickupOrDropoffEvent.value,
  },
]);

const formattedLocation = computed((): string => {
  const city = shipmentEvent.value.actualLocation.city;
  const state = shipmentEvent.value.actualLocation.state;

  if (city && state) {
    return `${city}, ${state}`;
  } else if (state) {
    return state;
  } else {
    return t('common.noParam', { param: t('locations.locations') });
  }
});

const formattedDate = computed(() => {
  const date =
    shipmentEvent.value.actualDate ??
    shipmentEvent.value.expectedDateRange?.end ??
    shipmentEvent.value.expectedDateRange?.start;
  const timeZone = shipmentEvent.value.actualLocation.timeZone;
  return renderDateTime(date, { separator: '@', timeZone });
});

const milestoneIcon = computed(() => {
  switch (shipmentEvent.value.eventType) {
    case ShipmentEventType.CARRIER_PICKED_UP:
      return PickupIcon;
    case ShipmentEventType.DROPOFF:
      return DropoffIcon;
    default:
      return undefined;
  }
});
</script>

<style>
.shipment-event {
  &.milestone {
    --base-shipment-timeline-item-header-font-size: 16px;
  }

  .conduit-logo {
    height: var(--base-shipment-timeline-item-header-font-size);
    width: auto;
  }
}
</style>
